/**
 * Image library — upload images once, reuse them in any template.
 *
 * A slide-in panel shared by every screen that needs an image URL (the template
 * editor, the personalised-campaign brief). It is not a Marionette view: the
 * host view keeps its own `events` hash untouched, the panel binds its handlers
 * on its own root and removes itself on close.
 *
 *   ImageLibrary.open(view.$el, { onPick: function (image) { … } });
 *
 * `onPick` receives the image ({ url, name, width, height… }); what picking
 * means is the host's business (fill a field, insert a block, insert a tag).
 * A host can add a second, quieter action per image with `secondary`
 * ({ label, onPick }) — the editor uses it for "Copy URL".
 *
 * The backend is the judge of what is acceptable (PNG / JPEG / GIF, 5 MB); the
 * checks here only spare the user a pointless upload.
 */
define('mailingImageLibrary',[
  'module',
  'underscore',
  'jquery',
  'mailingShell',
  'entities/mailing'
], function (
  module,
  _,
  $,
  Shell,
  MailingApi
) {
  'use strict';

  var MAX_BYTES = 5 * 1024 * 1024;
  var ACCEPTED = ['image/png', 'image/jpeg', 'image/gif'];

  function formatSize(bytes) {
    var kb = (Number(bytes) || 0) / 1024;
    return kb >= 1024 ? (Math.round(kb / 102.4) / 10) + ' MB' : Math.max(1, Math.round(kb)) + ' KB';
  }

  /** Why this file should not even be sent, or null. */
  function refusal(file) {
    if (file.type && _.indexOf(ACCEPTED, file.type) === -1) {
      return '"' + file.name + '": only PNG, JPEG and GIF display reliably in mail clients';
    }
    if (file.size > MAX_BYTES) {
      return '"' + file.name + '" is larger than 5 MB — too heavy for an email';
    }
    return null;
  }

  var ImageLibrary = {

    refusal: refusal,

    /**
     * @param $root   element of the host view; the panel is appended to it and
     *                feedback goes to its .mlg-banner
     * @param options { onPick: function (image), pickLabel: string,
     *                  secondary: { label: string, onPick: function (image) } }
     */
    open: function ($root, options) {
      options = options || {};
      $root.find('.mlg-images-panel').remove();

      var $panel = $(
        '<div class="mlg-images-panel">' +
        '<div class="mlg-images-card">' +
        '  <div class="i-title">Images<button type="button" class="i-close" title="Close">&times;</button></div>' +
        '  <div class="i-drop">' +
        '    <input type="file" class="i-file" accept="image/png,image/jpeg,image/gif" multiple style="display:none">' +
        '    <button type="button" class="mlg-btn primary i-browse">Upload images…</button>' +
        '    <div class="mlg-hint">or drop files here — PNG, JPEG or GIF, 5 MB max each.</div>' +
        '  </div>' +
        '  <div class="i-status"></div>' +
        '  <div class="i-grid"><div class="mlg-loading">Loading images…</div></div>' +
        '</div>' +
        '</div>').appendTo($root);

      var images = [];
      var $grid = $panel.find('.i-grid');
      var $status = $panel.find('.i-status');

      function render() {
        if (!images.length) {
          $grid.html(Shell.emptyHtml('No image yet. Upload one to use it in your templates.'));
          return;
        }
        $grid.html(_.map(images, function (image) {
          return '<div class="i-item" data-secid="' + image.secId + '">' +
            '<button type="button" class="i-pick" title="' + _.escape(options.pickLabel || 'Use this image') + '">' +
            '  <img src="' + _.escape(Shell.thumbnailUrl(image.url)) + '" alt="" loading="lazy">' +
            '</button>' +
            '<div class="i-name" title="' + _.escape(image.name) + '">' + _.escape(image.name) + '</div>' +
            (options.secondary ? '<button type="button" class="i-secondary">' +
              _.escape(options.secondary.label) + '</button>' : '') +
            '<div class="i-meta">' + image.width + '×' + image.height + ' · ' + formatSize(image.sizeBytes) +
            '  <button type="button" class="i-delete" title="Remove from the library">&times;</button></div>' +
            '</div>';
        }).join(''));
      }

      function close() {
        $panel.remove();
      }

      /** One request per file, in sequence: a failure names its file and the rest still go through. */
      function upload(files) {
        var queue = _.toArray(files);
        var failures = [];
        var done = 0;
        var total = queue.length;
        if (!total) { return; }

        function next() {
          var file = queue.shift();
          if (!file) {
            // Failures stay in the panel as well: opened from inside a modal,
            // the page banner sits behind the overlay.
            $status.toggleClass('error', failures.length > 0).text(failures.join(' · '));
            if (failures.length) {
              Shell.banner($root, failures.join(' · '), 'error');
            } else {
              $status.text(done + ' image' + (done === 1 ? '' : 's') + ' uploaded');
              Shell.banner($root, done + ' image' + (done === 1 ? '' : 's') + ' uploaded', 'ok');
            }
            return;
          }
          var problem = refusal(file);
          if (problem) {
            failures.push(problem);
            next();
            return;
          }
          $status.removeClass('error').text('Uploading ' + (done + failures.length + 1) + ' of ' + total + '…');
          MailingApi.uploadImage(file).done(function (image) {
            done++;
            images.unshift(image);
            render();
          }).fail(function (xhr) {
            failures.push('"' + file.name + '": ' + MailingApi.errorMessage(xhr));
          }).always(next);
        }
        next();
      }

      $panel.on('click', function (e) {
        if (e.target === $panel[0]) { close(); }
      });
      $panel.on('click', '.i-close', close);
      $panel.on('click', '.i-browse', function () { $panel.find('.i-file').click(); });
      $panel.on('change', '.i-file', function () {
        upload(this.files);
        this.value = '';   // so picking the same file again still fires "change"
      });
      $panel.on('dragover', '.mlg-images-card', function (e) {
        e.preventDefault();
        $panel.find('.i-drop').addClass('over');
      });
      $panel.on('dragleave drop', '.mlg-images-card', function () {
        $panel.find('.i-drop').removeClass('over');
      });
      $panel.on('drop', '.mlg-images-card', function (e) {
        e.preventDefault();
        var transfer = e.originalEvent.dataTransfer;
        if (transfer && transfer.files) { upload(transfer.files); }
      });
      $panel.on('click', '.i-pick', function () {
        var secId = $(this).closest('.i-item').data('secid');
        var image = _.findWhere(images, { secId: secId });
        close();
        if (image && options.onPick) { options.onPick(image); }
      });
      // The panel stays open: copying a URL is typically followed by another look.
      $panel.on('click', '.i-secondary', function () {
        var image = _.findWhere(images, { secId: $(this).closest('.i-item').data('secid') });
        if (image) { options.secondary.onPick(image, $status); }
      });
      $panel.on('click', '.i-delete', function () {
        var secId = $(this).closest('.i-item').data('secid');
        var image = _.findWhere(images, { secId: secId }) || {};
        if (!window.confirm('Remove "' + (image.name || 'this image') + '" from the library?\n\n' +
            'Templates and emails already using it keep working — only the library entry goes.')) {
          return;
        }
        MailingApi.deleteImage(secId).done(function () {
          images = _.reject(images, function (candidate) { return candidate.secId === secId; });
          render();
        }).fail(function (xhr) {
          $status.addClass('error').text(MailingApi.errorMessage(xhr));
          Shell.banner($root, MailingApi.errorMessage(xhr), 'error');
        });
      });

      MailingApi.images().done(function (list) {
        images = list || [];
        render();
      }).fail(function (xhr) {
        $grid.html('<div class="mlg-error">' + _.escape(MailingApi.errorMessage(xhr)) + '</div>');
      });
    }
  };

  module.exports = ImageLibrary;
});

